import { RegionSetStatusType } from "@app/projects/enumerators";
import { AggregateDisplayType, SpecialtyGroupEntityType } from "@app/projects/literals";
import {
  AnalysisSpecialtyGroup,
  ResultRegion,
} from "@app/projects/results/common";
import {
  CompositeAggregateResultGroup,
  CompositeRegionSetAggregate,
  CompositeSpecialtyGroupAggregate,
} from "@app/projects/results/composite";
import { RegionGranularity } from "@app/region-sets";

interface CompositeAggregate {
  key: string;
  defaultDisplayOrder: number;
  displayText: string;
  linkSuffix: string;

  providerCount: number;
  accuracyIndex: number;
  providerPassPercentage: number;
  providerFailPercentage: number;

  valid?: number;
  deactivated?: number;
  OIGExclusion?: number;
  validated?: number;
  invalidated?: number;
  suspectNumber?: number;
  attestedPercent?: number;
  geocoded?: number;

  distinctNPINumAttested: number;
  distinctNPIPercentAttested: number;
  distinctNPINumUnattested: number;
  numAttested: number;
  numUnattested: number;
  percentAttested: number;
  resultGroups: CompositeAggregateResultGroup[];

  regionCode: string;
  regionSetId: number;
  regionSetDisplayName: string;
  regionStatus?: RegionSetStatusType;
  regionGranularity: RegionGranularity;
  subRegionCode: string;

  specialtyGroupCode?: string;
  specialtyGroupDisplayName?: string;
  specialtyGroupDisplayOrder?: number;
  specialtyGroupEntityType?: SpecialtyGroupEntityType;
  specialtyGroupIsInformational?: boolean;
  specialtyGroupName?: string;

  accessTotalCount: number;
  accessMetCount: number;
  accessNotMetCount: number;

  servicingTotalCount: number;
  servicingMetCount: number;
  servicingNotMetCount: number;

  exceptionCount?: number;
  approvedExceptionCount: number;

  accessOnlyMetCount?: number;
  servicingOnlyMetCount: number;
  noneMetCount: number;

  percentPassBoth: number;
  percentPassAccessOnly?: number;
  percentPassServicingOnly: number;
  percentPassNone: number;
  percentPassByException: number;

  adequacyIndex: number;
  adequacyIndexWithExceptions: number;
  adequacyIndexInformationalOnly: number;
  adequacyIndexWithInformational: number;

  recentlyAttestedUniqueNPICount?: number;
  recentlyAttestedUniqueNPIPercent?: number;

  fourStarsGreaterAnnually?: number;
  lessThanFourStarsAnnually?: number;
  penetration?: number;

  projectedAdequacyIndex?: number;
  projectedPercentPassBoth?: number;

  gapCount: number;
  noGapCount?: number;
  unfillableGapCount?: number;
  partiallyFillableGapCount?: number;
  fillableGapCount?: number;

  parentAbbreviation?: string;
  parentDisplayName?: string;
}

class CompositeAggregateImpl implements CompositeAggregate {
  public constructor(
    public key: string,
    private aggregateDisplayType: AggregateDisplayType,

    public providerCount: number,
    public accuracyIndex: number,
    public providerPassPercentage: number,
    public providerFailPercentage: number,

    public valid: number,
    public deactivated: number,
    public OIGExclusion: number,
    public validated: number,
    public invalidated: number,
    public suspectNumber: number,
    public attestedPercent: number,
    public geocoded: number,

    public distinctNPINumAttested: number,
    public distinctNPIPercentAttested: number,
    public distinctNPINumUnattested: number,
    public numAttested: number,
    public numUnattested: number,
    public percentAttested: number,
    public resultGroups: CompositeAggregateResultGroup[],

    public regionCode: string,
    public regionGranularity: RegionGranularity,
    public regionSetDisplayName: string,
    public regionSetId: number,
    public subRegionCode: string,

    public accessMetCount: number,
    public accessNotMetCount: number,
    public accessTotalCount: number,

    public servicingMetCount: number,
    public servicingNotMetCount: number,
    public servicingTotalCount: number,

    public approvedExceptionCount: number,

    public servicingOnlyMetCount: number,
    public noneMetCount: number,

    public percentPassByException: number,
    public percentPassBoth: number,
    public percentPassServicingOnly: number,
    public percentPassNone: number,

    public adequacyIndex: number,
    public adequacyIndexInformationalOnly: number,
    public adequacyIndexWithExceptions: number,
    public adequacyIndexWithInformational: number,

    public gapCount: number,

    public regionStatus?: RegionSetStatusType,

    public specialtyGroupCode?: string,
    public specialtyGroupDisplayName?: string,
    public specialtyGroupDisplayOrder?: number,
    public specialtyGroupEntityType?: SpecialtyGroupEntityType,
    public specialtyGroupIsInformational?: boolean,
    public specialtyGroupName?: string,

    public recentlyAttestedUniqueNPICount?: number,
    public recentlyAttestedUniqueNPIPercent?: number,

    public fourStarsGreaterAnnually?: number,
    public lessThanFourStarsAnnually?: number,
    public penetration?: number,
    public projectedAdequacyIndex?: number,

    public percentPassAccessOnly?: number,
    public exceptionCount?: number,
    public accessOnlyMetCount?: number,
    public noGapCount?: number,
    public unfillableGapCount?: number,
    public partiallyFillableGapCount?: number,
    public fillableGapCount?: number,

    public parentAbbreviation?: string,
    public parentDisplayName?: string,
  ) { }

  public get defaultDisplayOrder(): number {
    switch (this.aggregateDisplayType) {
      case "region":
        return this.regionSetId;
      case "specialty":
        return this.specialtyGroupDisplayOrder;
      case "unknown":
        return null;
    }
  }

  public get displayText(): string {
    switch (this.aggregateDisplayType) {
      case "region":
        return this.regionSetDisplayName;
      case "specialty":
        return this.specialtyGroupDisplayName;
      case "unknown":
        return null;
    }
  }

  public get linkSuffix(): string {
    switch (this.aggregateDisplayType) {
      case "region":
        return `/${this.regionGranularity}/${this.regionCode}`;
      case "specialty":
        return `/specialty/${this.specialtyGroupCode}`;
      case "unknown":
        return null;
    }
  }
}

function buildCompositeAggregate(
  key: string,
  resultRegion: ResultRegion,
  aggregate: CompositeRegionSetAggregate | CompositeSpecialtyGroupAggregate,
  aggregateDisplayType: AggregateDisplayType,
  specialtyGroup?: AnalysisSpecialtyGroup,
  regionStatus?: RegionSetStatusType,
): CompositeAggregate {
  if (!resultRegion || !aggregate) {
    return {} as CompositeAggregate;
  }
  if (isSpecialtyGroupAggregate(aggregate)) {
    //A specialty group is required and only used when passing in an aggregate of this type
    //we're not checking if it (specialty group) exists bc we want it to fail if it doesn't exist
    return new CompositeAggregateImpl(
      key,
      aggregateDisplayType,

      aggregate.providerCount,
      aggregate.accuracyIndex,
      aggregate.providerPassPercentage,
      aggregate.providerFailPercentage,

      aggregate.valid,
      aggregate.deactivated,
      aggregate.OIGExclusion,
      aggregate.validated,
      aggregate.invalidated,
      aggregate.suspectNumber,
      aggregate.attestedPercent,
      aggregate.geocoded,

      aggregate.distinctNPINumAttested,
      aggregate.distinctNPIPercentAttested,
      aggregate.distinctNPINumUnattested,
      aggregate.numAttested,
      aggregate.numUnattested,
      aggregate.percentAttested,
      aggregate.resultGroups,

      resultRegion.regionCode,
      resultRegion.regionGranularity,
      resultRegion.displayName,
      resultRegion.regionSetId,
      resultRegion.subRegionCode,

      aggregate.accessMetCount,
      aggregate.accessNotMetCount,
      aggregate.accessTotalCount,

      aggregate.servicingMetCount,
      aggregate.servicingNotMetCount,
      aggregate.servicingTotalCount,

      aggregate.approvedExceptionCount || 0,

      aggregate.servicingOnlyMetCount,
      aggregate.noneMetCount,

      aggregate.percentPassByException,
      aggregate.percentPassBoth,
      aggregate.percentPassServicingOnly,
      aggregate.percentPassNone,

      aggregate.adequacyIndex,
      aggregate.adequacyIndexInformationalOnly,
      aggregate.adequacyIndexWithExceptions,
      aggregate.adequacyIndexWithInformational,

      aggregate.gapCount,

      regionStatus,

      specialtyGroup.code,
      `${specialtyGroup.code} - ${specialtyGroup.name}`,
      specialtyGroup.displayOrder,
      specialtyGroup.entityType,
      specialtyGroup.isInformational,
      specialtyGroup.name,

      aggregate.recentlyAttestedUniqueNPICount,
      aggregate.recentlyAttestedUniqueNPIPercent,

      undefined,
      undefined,
      undefined,
      aggregate.projectedAdequacyIndex,

      aggregate.percentPassAccessOnly || 0,
      aggregate.exceptionCount,
      aggregate.accessOnlyMetCount,
      aggregate.noGapCount,
      aggregate.unfillableGapCount,
      aggregate.partiallyFillableGapCount,
      aggregate.fillableGapCount,

      aggregate.parentAbbreviation,
      aggregate.parentDisplayName,
    );

  } else {
    const [valid, deactivated, OIGExclusion, validated, invalidated, suspectNumber, attestedPercent, geocoded]:
    [number, number, number, number, number, number, number, number] = [null, null, null, null, null, null, null, null];

    //if (aggregate.resultGroups && aggregate.resultGroups.filter((r) => r.results).length > 0) {
    //  aggregate.resultGroups.filter((r) => r.results).forEach((rg) => {
    //    rg.results.forEach((r) => {
    //      switch (r.name) {
    //        case InspectionResultType.Valid: {
    //          valid = r.value;
    //        }
    //        case InspectionResultType.Deactivated: {
    //          deactivated = r.value;
    //        }
    //        case InspectionResultType.OIGExclusion: {
    //          OIGExclusion = r.value;
    //        }
    //        case InspectionResultType.Validated: {
    //          validated = r.value;
    //        }
    //        case InspectionResultType.Invalidated: {
    //          invalidated = r.value;
    //        }
    //        case InspectionResultType.SuspectNumber: {
    //          suspectNumber = r.value;
    //        }
    //        case InspectionResultType.AttestedPercent: {
    //          attestedPercent = r.value;
    //        }
    //        case InspectionResultType.Geocoded: {
    //          geocoded = r.value;
    //        }
    //      }
    //    });
    //  });
    //}

    return new CompositeAggregateImpl(
      key,
      aggregateDisplayType,

      aggregate.providerCount,
      aggregate.accuracyIndex,
      aggregate.providerPassPercentage,
      aggregate.providerFailPercentage,

      valid,
      deactivated,
      OIGExclusion,
      validated,
      invalidated,
      suspectNumber,
      attestedPercent,
      geocoded,

      aggregate.distinctNPINumAttested,
      aggregate.distinctNPIPercentAttested,
      aggregate.distinctNPINumUnattested,
      aggregate.numAttested,
      aggregate.numUnattested,
      aggregate.percentAttested,
      aggregate.resultGroups,

      resultRegion.regionCode,
      resultRegion.regionGranularity,
      resultRegion.displayName,
      resultRegion.regionSetId,
      resultRegion.subRegionCode,

      aggregate.accessMetCount,
      aggregate.accessNotMetCount,
      aggregate.accessTotalCount,

      aggregate.servicingMetCount,
      aggregate.servicingNotMetCount,
      aggregate.servicingTotalCount,

      aggregate.approvedExceptionCount || 0,

      aggregate.servicingOnlyMetCount,
      aggregate.noneMetCount,

      aggregate.percentPassByException,
      aggregate.percentPassBoth,
      aggregate.percentPassServicingOnly,
      aggregate.percentPassNone,

      aggregate.adequacyIndex,
      aggregate.adequacyIndexInformationalOnly,
      aggregate.adequacyIndexWithExceptions,
      aggregate.adequacyIndexWithInformational,

      aggregate.gapCount,

      regionStatus,

      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,

      aggregate.recentlyAttestedUniqueNPICount,
      aggregate.recentlyAttestedUniqueNPIPercent,

      aggregate.fourStarsGreaterAnnually,
      aggregate.lessThanFourStarsAnnually,
      aggregate.penetration,
      aggregate.projectedAdequacyIndex,

      aggregate.percentPassAccessOnly || 0,
      aggregate.exceptionCount,
      aggregate.accessOnlyMetCount,
      aggregate.noGapCount,
      aggregate.unfillableGapCount,
      aggregate.partiallyFillableGapCount,
      aggregate.fillableGapCount,

      aggregate.parentAbbreviation,
      aggregate.parentDisplayName,
    );
  }

}

/**
 * Clones an `CompositeAggregate` while setting a new displayText property based on the `CompositeAggregateDisplayType`
 * It DOES actually create a new object
 * @param aggregate
 * @param aggregateDisplayType
 */
function pseudoClone(
  aggregate: CompositeAggregate,
  aggregateDisplayType: AggregateDisplayType,
): CompositeAggregate {

  return new CompositeAggregateImpl(
    aggregate.key,
    aggregateDisplayType,

    aggregate.providerCount,
    aggregate.accuracyIndex,
    aggregate.providerPassPercentage,
    aggregate.providerFailPercentage,

    aggregate.valid,
    aggregate.deactivated,
    aggregate.OIGExclusion,
    aggregate.validated,
    aggregate.invalidated,
    aggregate.suspectNumber,
    aggregate.attestedPercent,
    aggregate.geocoded,

    aggregate.distinctNPINumAttested,
    aggregate.distinctNPIPercentAttested,
    aggregate.distinctNPINumUnattested,
    aggregate.numAttested,
    aggregate.numUnattested,
    aggregate.percentAttested,
    aggregate.resultGroups,

    aggregate.regionCode,
    aggregate.regionGranularity,
    aggregate.regionSetDisplayName,
    aggregate.regionSetId,
    aggregate.subRegionCode,

    aggregate.accessMetCount,
    aggregate.accessNotMetCount,
    aggregate.accessTotalCount,

    aggregate.servicingMetCount,
    aggregate.servicingNotMetCount,
    aggregate.servicingTotalCount,

    aggregate.approvedExceptionCount,

    aggregate.servicingOnlyMetCount,
    aggregate.noneMetCount,

    aggregate.percentPassByException,
    aggregate.percentPassBoth,
    aggregate.percentPassServicingOnly,
    aggregate.percentPassNone,

    aggregate.adequacyIndex,
    aggregate.adequacyIndexInformationalOnly,
    aggregate.adequacyIndexWithExceptions,
    aggregate.adequacyIndexWithInformational,

    aggregate.gapCount,

    aggregate.regionStatus,

    aggregate.specialtyGroupCode,
    aggregate.specialtyGroupDisplayName,
    aggregate.specialtyGroupDisplayOrder,
    aggregate.specialtyGroupEntityType,
    aggregate.specialtyGroupIsInformational,
    aggregate.specialtyGroupName,

    aggregate.recentlyAttestedUniqueNPICount,
    aggregate.recentlyAttestedUniqueNPIPercent,

    aggregate.fourStarsGreaterAnnually,
    aggregate.lessThanFourStarsAnnually,
    aggregate.penetration,
    aggregate.projectedAdequacyIndex,

    aggregate.percentPassAccessOnly || 0,
    aggregate.exceptionCount,
    aggregate.accessOnlyMetCount,
    aggregate.noGapCount,
    aggregate.unfillableGapCount,
    aggregate.partiallyFillableGapCount,
    aggregate.fillableGapCount,

    aggregate.parentAbbreviation,
    aggregate.parentDisplayName,
  );

}

function isSpecialtyGroupAggregate(aggregate: CompositeRegionSetAggregate | CompositeSpecialtyGroupAggregate): aggregate is CompositeSpecialtyGroupAggregate {
  return (aggregate as CompositeSpecialtyGroupAggregate).specialtyGroupCode !== undefined;
}

export { CompositeAggregate, buildCompositeAggregate, pseudoClone };
